import { useEffect } from 'react';
import { Toaster, toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { Bell, CheckCircle, AlertTriangle, Info, XCircle } from 'lucide-react';

const notificationSound = new Audio('/notification.mp3');

const notificationIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle
};

export default function NotificationProvider() {
  useEffect(() => {
    const setupNotificationSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Preload notification sound
      notificationSound.load();

      const channel = supabase
        .channel('notifications_changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            const notification = payload.new as any;
            
            // Play sound with user interaction check
            const playSound = () => {
              notificationSound.play().catch(error => {
                if (error.name === 'NotAllowedError') {
                  // If autoplay is blocked, we'll play on next user interaction
                  document.addEventListener('click', () => {
                    notificationSound.play().catch(console.warn);
                  }, { once: true });
                } else {
                  console.warn('Failed to play notification sound:', error);
                }
              });
            };

            // Check if the document has been interacted with
            if (document.documentElement.classList.contains('user-interacted')) {
              playSound();
            } else {
              // Wait for first interaction
              document.addEventListener('click', playSound, { once: true });
            }

            // Get the appropriate icon component
            const IconComponent = notificationIcons[notification.type as keyof typeof notificationIcons] || Info;

            // Show toast notification
            toast(notification.title, {
              description: notification.message,
              icon: <IconComponent className={`w-5 h-5 ${
                notification.type === 'success' ? 'text-green-600' :
                notification.type === 'warning' ? 'text-yellow-600' :
                notification.type === 'error' ? 'text-red-600' :
                'text-blue-600'
              }`} />,
              duration: 5000,
              className: `${
                notification.type === 'success' ? 'border-green-100' :
                notification.type === 'warning' ? 'border-yellow-100' :
                notification.type === 'error' ? 'border-red-100' :
                'border-blue-100'
              }`,
            });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    // Add interaction detector
    const handleFirstInteraction = () => {
      document.documentElement.classList.add('user-interacted');
      document.removeEventListener('click', handleFirstInteraction);
    };
    document.addEventListener('click', handleFirstInteraction);

    setupNotificationSubscription();

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
    };
  }, []);

  return (
    <Toaster
      position="top-right"
      expand={true}
      richColors
      closeButton
      theme="light"
      toastOptions={{
        className: 'notification-toast',
        style: {
          background: 'white',
          border: '1px solid',
          borderRadius: '0.75rem',
          padding: '1rem',
        },
      }}
    />
  );
}